// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import { CustomCsvDownloadButton } from 'components/atoms/CustomCsvDownloadButton';
import { CalendarField } from 'components/molecules/CalendarField';
import { AdminTemplate } from 'components/templates/AdminTemplate';
import DOMPurify from 'dompurify';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getSlaYMDhm } from 'ts/formatDate';
import './WeightNotices.scss';
import { ImageViewerModal } from 'components/organisms/ImageViewerModal';
import { getNotices } from 'apis/notices';
import { WeightNoticeItem, WeightNoticeResponse } from 'types/notice';
import CustomPagination from 'components/atoms/CustomPagination';
import { DEFAULT_PAGE, PAGE_SIZE } from 'util/ConstantValues';
import Csv from 'util/csv';

type OpeningImageState =
  | {
      images: string[];
      index: number;
    }
  | undefined;

const WeightNotices = () => {
  const [weightNotices, setWeightNotices] = useState<WeightNoticeItem[]>([]);
  const { t } = useTranslation();
  const [openingImage, setOpeningImage] = useState<OpeningImageState>();
  const tableRef = useRef<HTMLTableElement>(null);
  const [targetDateSearch, setTargetDateSearch] = useState<string | null>(null);
  const [pageMeta, setPageMeta] = useState<{
    page: number;
    total: number;
  }>({
    page: 1,
    total: 0,
  });

  const pageCount = useMemo(
    () => Math.ceil(pageMeta.total / PAGE_SIZE),
    [pageMeta.total]
  );

  const resetPageMeta = () => {
    setPageMeta({
      page: 1,
      total: 0,
    });
  };

  const callApi = async (date: string | null, page = pageMeta.page) => {
    getNotices({
      params: {
        page,
        take: PAGE_SIZE,
        targetDate: date || undefined,
      },
      successCallback: (data) => {
        setWeightNotices((data as WeightNoticeResponse).notices);
        setPageMeta({
          page: (data as WeightNoticeResponse).page,
          total: (data as WeightNoticeResponse).total,
        });
      },
      failedCallback: () => {
        setWeightNotices([]);
        resetPageMeta();
      },
    });
  };

  useEffect(() => {
    callApi(null);
  }, []);

  const csvHeaders: (string | [string, string])[] = useMemo(() => {
    return [
      t('notification.thead_datetime'),
      t('notification.thead_thead_person'),
      t('notification.thead_place'),
      t('notification.thead_registerd_content'),
      t('notification.thead_notification_content'),
      t('notification.thead_repair_status'),
      t('notification.thead_remarks'),
    ] as string[];
  }, [t]);

  const handleDownloadCsv = () => {
    getNotices({
      params: {
        targetDate: targetDateSearch || undefined,
      },
      successCallback: (data) => {
        new Csv({
          headers: csvHeaders,
          data:
            (data as WeightNoticeItem[]).map((item) => [
              getSlaYMDhm(item.datetime),
              item.userId + '(' + item.userName + ')',
              item.areaName,
              item.description,
              item.type,
              item.collect,
              item.note,
            ]) || [],
        }).downLoadCsv({
          filename: '通知リスト',
        });
      },
    });
  };

  const handlePageClick = (selectedItem: { selected: number }) => {
    tableRef.current?.scrollTo({
      top: 0,
      behavior: 'instant' as ScrollBehavior,
    });
    callApi(targetDateSearch, selectedItem.selected + 1);
  };

  return (
    <AdminTemplate
      h1={t('common.sidebar_notification')}
      active="通知リスト"
      breadcrumb={[{ label: t('common.sidebar_notification') }]}
      className="weight-notices-page"
    >
      <div
        className={'countList'}
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(
            t('common.page', {
              page: weightNotices.length || 0,
            }) ?? ''
          ),
        }}
      />
      <div className="search-area">
        <CalendarField
          maxDate={new Date()}
          onChange={(value) => {
            setTargetDateSearch(value);
            resetPageMeta();
            callApi(value, DEFAULT_PAGE);
          }}
        />
        <CustomCsvDownloadButton onClick={handleDownloadCsv} />
      </div>

      <div className="scroll-wrapper">
        <table id="notices-table" ref={tableRef}>
          <thead>
            <tr>
              <th className="c--date">{t('notification.thead_datetime')}</th>
              <th className="c--staff">
                {t('notification.thead_thead_person')}
              </th>
              <th className="c--area">{t('notification.thead_place')}</th>
              <th className="c--description">
                {t('notification.thead_registerd_content')}
              </th>
              <th className="c--type">
                {t('notification.thead_notification_content')}
              </th>
              <th className="c--collect">{t('notification.thead_collect')}</th>
              <th className="c--note">{t('notification.thead_remarks')}</th>
              <th>画像</th>
            </tr>
          </thead>
          <tbody>
            {weightNotices.map((item, i) => {
              return (
                <tr key={i}>
                  <td className="c--date">
                    {getSlaYMDhm(item.datetime) || '-'}
                  </td>
                  <td className="left c--staff">
                    {item.userId + '(' + item.userName + ')' || '-'}
                  </td>
                  <td className="left c--area">{item.areaName || '-'}</td>
                  <td className="left c--description">
                    {item.description || '-'}
                  </td>
                  <td className="left c--type">{item.type || '-'}</td>
                  <td className="left c--collect">{item.collect || '-'}</td>
                  <td className="left c--note">{item.note}</td>
                  <td className="left c--images">
                    <div className="image-wrapper">
                      {item.imageUrls?.map((url, idx) => (
                        <img
                          key={idx}
                          src={`${process.env.REACT_APP_CLOUDFRONT}${url}`}
                          alt={`notice-${item.id}-${idx}`}
                          className="notice-image"
                          onClick={() => {
                            if (item.imageUrls) {
                              setOpeningImage({
                                images: item.imageUrls.map(
                                  (u) =>
                                    `${process.env.REACT_APP_CLOUDFRONT}${u}`
                                ),
                                index: idx,
                              });
                            } else {
                              setOpeningImage(undefined);
                            }
                          }}
                        />
                      ))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {pageMeta.total > 0 && (
        <div className="paginationContainer">
          <CustomPagination
            mode="light"
            pageCount={pageCount}
            onPageChange={handlePageClick}
            forcePage={pageMeta.page - 1}
          />
        </div>
      )}
      {openingImage && (
        <ImageViewerModal
          images={openingImage.images}
          currentIndex={openingImage.index}
          onClose={() => setOpeningImage(undefined)}
          onIndexChange={(index) =>
            setOpeningImage((prev) => prev && { ...prev, index })
          }
        />
      )}
    </AdminTemplate>
  );
};

export default WeightNotices;
